// client/src/pages/CampaignBuilder.js
// Create New Campaign — multi-step wizard builder.
//
// Route: /project/:projectId/campaign-builder
// Wrapped in SidebarLayout for full navigation.
//
// Layout follows GJP_New_Campaign_Set_Up_Layout (v5) exactly:
//   Step 1: Campaign Name, Brand, Domain, Target Market
//   Step 2: Modules selection
//   Step 3: Prompts (Add manually / Import from ENQ) + Engines & Frequency
//   Step 4: URLs
//   Step 5: Competitors (+ AI suggestions option)
//   Step 6: Review & Submit
//
// Styling matches MyURLsPage / CampaignSummary: Work Sans, #048ba8 teal,
// white cards on #f9fafb, 1px #e5e7eb borders, 8px radius.
//
// UPDATED Aug 24 2026: On success, use a full page load instead of navigate().
//                      App.js fetches its `campaigns` array once on mount and never
//                      refetches, and CampaignRouter redirects to "/" when it can't find
//                      the campaignId in that array. A client-side navigate() to a
//                      brand-new campaign therefore bounced the user straight back to the
//                      projects list, making a successful creation look like a failure.
// UPDATED Aug 24 2026: Added Target Market to step 1. The backend has always accepted and
//                      validated `target_market` against ALLOWED_MARKETS; this wizard just
//                      never sent it. Defaults to United States, matching the backend's own
//                      default — the user changes it per campaign.
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import SidebarLayout from '../components/SidebarLayout';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://geojetpack-production.up.railway.app'}/api`;

// Load Work Sans, matching MyURLsPage
if (typeof document !== 'undefined' && !document.querySelector('link[href*="Work+Sans"]')) {
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
  fontLink.rel = 'stylesheet';
  document.head.appendChild(fontLink);
}

const colors = {
  primary:   '#39393a',
  teal:      '#048ba8',
  tertiary:  '#e9c46a',
  accent:    '#f4a261',
  coral:     '#e76f51',
  green:     '#01796f',
  textMuted: '#6b7280',
  border:    '#e5e7eb',
  bgSubtle:  '#f9fafb',
};

const FONT = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// NOTE: the backend's ALLOWED_ENGINES is still the legacy SEO-tool list
// (semrush/ahrefs/moz/similarweb), so sending the LLM engine keys below would 400.
// Leave this false until that allowlist is updated.
const SEND_ENGINES = false;

// MUST stay in sync with ALLOWED_MARKETS in api/campaigns/campaigns-create-UPDATED.js —
// the backend rejects anything not on that list. Order matches the backend exactly.
const MARKETS = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Japan',
  'Singapore',
  'India',
];

const DEFAULT_MARKET = 'United States';

const MODULES = [
  { key: 'prompt_visibility', label: 'Prompt Visibility & Citation', col: 0 },
  { key: 'content_audit',     label: 'Content Audit',                col: 0 },
  { key: 'knowledge_graph',   label: 'Knowledge Graph Audit',        col: 1 },
  { key: 'ai_reporting',      label: 'AI Reporting',                 col: 1 },
];

const ENGINES = [
  { key: 'chatgpt',    label: 'ChatGPT' },
  { key: 'claude',     label: 'Claude' },
  { key: 'gemini',     label: 'Gemini' },
  { key: 'perplexity', label: 'Perplexity' },
];

const FREQUENCIES = [
  { key: 'daily',       label: 'Daily',            hint: 'every 24h' },
  { key: 'three_daily', label: 'Every 3 days',     hint: 'every 72h' },
  { key: 'weekly',      label: 'Weekly',           hint: 'every 168h' },
  { key: 'fortnightly', label: 'Fortnightly',      hint: 'every 336h' },
];

// Helpers
const normaliseDomain = (raw) => {
  if (!raw) return '';
  let s = String(raw).trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.split('/')[0].split(':')[0];
  s = s.replace(/^www\./, '');
  return s;
};

const looksLikeDomain = (s) => /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s);

const parseCsv = (text, preferredHeaders) => {
  const lines = String(text).split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const strip = (v) => v.replace(/^"(.*)"$/, '$1').trim();
  const header = lines[0].split(',').map(h => strip(h).toLowerCase());
  const colIndex = header.findIndex(h => preferredHeaders.includes(h));
  if (colIndex === -1) return lines.map(l => strip(l.split(',')[0])).filter(Boolean);
  return lines.slice(1)
    .map(l => strip((l.split(',')[colIndex] || '')))
    .filter(Boolean);
};

// UI Components
const Card = ({ children, style = {} }) => (
  <div style={{
    backgroundColor: 'white',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    ...style,
  }}>
    {children}
  </div>
);

const TextInput = ({ value, onChange, placeholder, ...rest }) => (
  <input
    type="text"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '10px 12px', fontSize: '14px', fontFamily: FONT,
      color: colors.primary, backgroundColor: 'white',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px', outline: 'none',
    }}
    onFocus={(e) => { e.target.style.borderColor = colors.teal; }}
    onBlur={(e) => { e.target.style.borderColor = colors.border; }}
    {...rest}
  />
);

const SelectInput = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '10px 12px', fontSize: '14px', fontFamily: FONT,
      color: colors.primary, backgroundColor: 'white',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px', outline: 'none', cursor: 'pointer',
    }}
    onFocus={(e) => { e.target.style.borderColor = colors.teal; }}
    onBlur={(e) => { e.target.style.borderColor = colors.border; }}
  >
    {options.map(opt => (
      <option key={opt} value={opt}>{opt}</option>
    ))}
  </select>
);

const ErrorCard = ({ message }) => (
  <Card style={{ borderColor: '#fca5a5', marginTop: '24px' }}>
    <div style={{ padding: '16px 20px', backgroundColor: '#fef2f2' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
        {message}
      </div>
    </div>
  </Card>
);

const ReviewRow = ({ label, value, last = false }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: '24px', padding: '12px 20px',
    borderBottom: last ? 'none' : `1px solid ${colors.border}`,
  }}>
    <span style={{ fontSize: '13px', color: colors.textMuted }}>{label}</span>
    <span style={{
      fontSize: '13px', fontWeight: 500, color: colors.primary,
      textAlign: 'right', maxWidth: '65%', wordBreak: 'break-word',
    }}>
      {value || '—'}
    </span>
  </div>
);

// Button styles
const buttonPrimary = {
  padding: '11px 22px',
  fontFamily: FONT,
  fontSize: '14px',
  fontWeight: 600,
  color: 'white',
  backgroundColor: colors.teal,
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const buttonSecondary = {
  padding: '11px 22px',
  fontFamily: FONT,
  fontSize: '14px',
  fontWeight: 500,
  color: colors.teal,
  backgroundColor: 'white',
  border: `1px solid ${colors.teal}`,
  borderRadius: '6px',
  cursor: 'pointer',
};

const buttonSmall = {
  marginTop: '2px', padding: '7px 14px',
  fontFamily: FONT, fontSize: '13px', fontWeight: 500,
  color: colors.teal, background: 'white',
  border: `1px solid ${colors.teal}`, borderRadius: '6px', cursor: 'pointer',
};

const buttonIcon = {
  flexShrink: 0, width: '32px', height: '32px',
  border: `1px solid ${colors.border}`, borderRadius: '6px',
  background: 'white', color: colors.textMuted,
  cursor: 'pointer', fontSize: '16px', lineHeight: 1,
};

// Main component
function CampaignBuilder({ apiBaseUrl: propApiBaseUrl, userId: propUserId }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const apiBase = propApiBaseUrl || API_BASE_URL;
  const userId = propUserId || user?.id;

  // Wizard state
  const [step, setStep] = useState(1);

  // Step 1: Basic Info
  const [campaignName, setCampaignName] = useState('');
  const [brand, setBrand] = useState('');
  const [domain, setDomain] = useState('');
  const [targetMarket, setTargetMarket] = useState(DEFAULT_MARKET);

  // Step 2: Modules
  const [modules, setModules] = useState([]);
  const [allModulesSelected, setAllModulesSelected] = useState(false);

  // Step 3: Prompts + Engines/Frequency
  const [promptSource, setPromptSource] = useState('manual');
  const [prompts, setPrompts] = useState(['', '']);
  const [entitySets, setEntitySets] = useState([]);
  const [entitySetId, setEntitySetId] = useState('');
  const [engines, setEngines] = useState(ENGINES.map(e => e.key));
  const [frequency, setFrequency] = useState('daily');

  // Step 4: URLs
  const [urls, setUrls] = useState(['', '']);

  // Step 5: Competitors
  const [competitors, setCompetitors] = useState(['', '']);

  // Status
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Sidebar
  const [allCampaigns, setAllCampaigns] = useState([]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${apiBase}/campaigns`, { headers: { 'x-user-id': userId } })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setAllCampaigns(Array.isArray(d) ? d : []))
      .catch(() => setAllCampaigns([]));
  }, [apiBase, userId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${apiBase}/entity-sets`, { headers: { 'x-user-id': userId } })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setEntitySets(Array.isArray(d) ? d : (d?.entity_sets || d?.entitySets || d?.data || [])))
      .catch(() => setEntitySets([]));
  }, [apiBase, userId]);

  // Validation
  const validateStep = () => {
    setError(null);

    switch (step) {
      case 1:
        if (!campaignName.trim()) {
          setError('Campaign name is required');
          return false;
        }
        if (!brand.trim()) {
          setError('Brand is required');
          return false;
        }
        if (!domain.trim()) {
          setError('Domain is required');
          return false;
        }
        if (!MARKETS.includes(targetMarket)) {
          setError('Please choose a target market');
          return false;
        }
        return true;

      case 2:
        if (!allModulesSelected && modules.length === 0) {
          setError('Please select at least one module');
          return false;
        }
        return true;

      case 3:
        const cleanPrompts = prompts.filter(p => p.trim());
        const usingEntitySet = promptSource === 'enq' && entitySetId;
        if (!usingEntitySet && cleanPrompts.length === 0) {
          setError('Please add at least one prompt');
          return false;
        }
        return true;

      case 4:
        const cleanUrls = urls.filter(u => u.trim());
        if (cleanUrls.length === 0) {
          setError('Please add at least one URL');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  // Module helpers
  const toggleAllModules = () => {
    setAllModulesSelected(!allModulesSelected);
    if (!allModulesSelected) {
      setModules([]);
    }
  };

  const toggleModule = (key) => {
    setModules(prev => {
      if (prev.includes(key)) {
        return prev.filter(m => m !== key);
      }
      return [...prev, key];
    });
    setAllModulesSelected(false);
  };

  const toggleEngine = (key) => {
    setEngines(prev =>
      prev.includes(key)
        ? prev.filter(e => e !== key)
        : [...prev, key]
    );
  };

  // Input list helpers
  const addPromptInput = () => setPrompts([...prompts, '']);
  const updatePromptInput = (idx, val) => {
    const updated = [...prompts];
    updated[idx] = val;
    setPrompts(updated);
  };
  const removePromptInput = (idx) => {
    const updated = prompts.filter((_, i) => i !== idx);
    setPrompts(updated.length ? updated : ['']);
  };

  const addUrlInput = () => setUrls([...urls, '']);
  const updateUrlInput = (idx, val) => {
    const updated = [...urls];
    updated[idx] = val;
    setUrls(updated);
  };
  const removeUrlInput = (idx) => {
    const updated = urls.filter((_, i) => i !== idx);
    setUrls(updated.length ? updated : ['']);
  };

  const addCompetitorInput = () => setCompetitors([...competitors, '']);
  const updateCompetitorInput = (idx, val) => {
    const updated = [...competitors];
    updated[idx] = val;
    setCompetitors(updated);
  };
  const removeCompetitorInput = (idx) => {
    const updated = competitors.filter((_, i) => i !== idx);
    setCompetitors(updated.length ? updated : ['']);
  };

  // CSV handlers
  const handlePromptsFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target?.result;
        if (typeof text === 'string') {
          const parsed = parseCsv(text, ['prompt_text', 'prompt', 'text']);
          if (parsed.length > 0) {
            setPrompts(parsed);
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const handleUrlsFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target?.result;
        if (typeof text === 'string') {
          const parsed = parseCsv(text, ['url', 'urls', 'page']);
          if (parsed.length > 0) {
            setUrls(parsed);
          }
        }
      };
      reader.readAsText(file);
    }
  };

  // Get selected modules
  const getSelectedModules = () => {
    if (allModulesSelected) {
      return ['all'];
    }
    return modules;
  };

  // Clean data
  const cleanPrompts = useMemo(() => prompts.map(p => p.trim()).filter(Boolean), [prompts]);
  const cleanUrls = useMemo(() => urls.map(u => u.trim()).filter(Boolean), [urls]);
  const cleanCompetitors = useMemo(() => competitors.map(normaliseDomain).filter(Boolean), [competitors]);

  const moduleLabel = (key) => MODULES.find(m => m.key === key)?.label || key;

  const getEntitySetName = (id) => {
    return entitySets.find(es => es.id === id)?.name || id;
  };

  // Convert frequency key to hours for display
  const frequencyLabel = () => {
    return FREQUENCIES.find(f => f.key === frequency)?.label || 'Daily';
  };

  const frequencyHours = () => {
    const map = {
      daily: 24,
      three_daily: 72,
      weekly: 168,
      fortnightly: 336,
    };
    return map[frequency] || 24;
  };

  // Submit
  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        name: campaignName.trim(),
        brand_name: brand.trim(),
        domain: domain.trim(),
        target_market: targetMarket,
        modules: getSelectedModules(),
        prompts: cleanPrompts,
        urls: cleanUrls,
        competitors: cleanCompetitors,
        project_id: projectId && projectId !== 'null' ? projectId : null,
        ...(SEND_ENGINES ? { engines: engines.length ? engines : ['all'] } : {}),
        ...(promptSource === 'enq' && entitySetId ? { entity_set_id: entitySetId } : {}),
        frequency,
        audit_frequency_hours: frequencyHours(),
      };

      const res = await fetch(`${apiBase}/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.details || `Request failed (${res.status})`);
      }

      const newId = data.campaign?.id || data.id;

      // Full page load, NOT navigate(). App.js loads its `campaigns` array once on
      // mount and never refetches, and CampaignRouter redirects to "/" when the
      // campaignId isn't in that array — so a client-side transition to a campaign
      // created seconds ago bounces the user back to the projects list.
      if (newId) {
        window.location.href = `/campaign/${newId}/summary`;
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setSubmitting(false);
    }
  };

  // Render by step
  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <div style={{ padding: '28px 32px', fontFamily: FONT, maxWidth: '600px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: colors.primary, margin: '0 0 24px' }}>
              Create New Campaign
            </h2>
            <Card>
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '8px' }}>
                    Campaign Name
                  </label>
                  <TextInput
                    value={campaignName}
                    onChange={setCampaignName}
                    placeholder="Enter campaign name"
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '8px' }}>
                    Brand
                  </label>
                  <TextInput
                    value={brand}
                    onChange={setBrand}
                    placeholder="Enter brand name"
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '8px' }}>
                    Domain
                  </label>
                  <TextInput
                    value={domain}
                    onChange={setDomain}
                    placeholder="Enter domain"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '8px' }}>
                    Target Market
                  </label>
                  <SelectInput
                    value={targetMarket}
                    onChange={setTargetMarket}
                    options={MARKETS}
                  />
                  <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '6px' }}>
                    The country the AI engines answer from. Changes which results count as visibility.
                  </div>
                </div>
              </div>
            </Card>
            {error && <ErrorCard message={error} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={handleNext} style={buttonPrimary}>
                Next
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ padding: '28px 32px', fontFamily: FONT, maxWidth: '600px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: colors.primary, margin: '0 0 24px' }}>
              Create New Campaign
            </h2>
            <Card>
              <div style={{ padding: '20px' }}>
                {MODULES.map(m => (
                  <label
                    key={m.key}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={allModulesSelected || modules.includes(m.key)}
                      onChange={() => toggleModule(m.key)}
                      disabled={allModulesSelected}
                      style={{ width: '18px', height: '18px', accentColor: colors.teal, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: colors.primary }}>{m.label}</span>
                  </label>
                ))}
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={allModulesSelected}
                    onChange={toggleAllModules}
                    style={{ width: '18px', height: '18px', accentColor: colors.teal, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: 500, color: colors.primary }}>All Modules</span>
                </label>
              </div>
            </Card>
            {error && <ErrorCard message={error} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={handleBack} style={buttonSecondary}>
                &lt;back to campaign name
              </button>
              <button type="button" onClick={handleNext} style={buttonPrimary}>
                Next
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ padding: '28px 32px', fontFamily: FONT, maxWidth: '600px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: colors.primary, margin: '0 0 24px' }}>
              Prompts
            </h2>
            <Card>
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '24px', display: 'flex', gap: '32px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="prompt-source"
                      value="manual"
                      checked={promptSource === 'manual'}
                      onChange={() => setPromptSource('manual')}
                    />
                    <span style={{ fontSize: '14px', color: colors.primary }}>Add manually</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="prompt-source"
                      value="enq"
                      checked={promptSource === 'enq'}
                      onChange={() => setPromptSource('enq')}
                    />
                    <span style={{ fontSize: '14px', color: colors.primary }}>Import from ENQ</span>
                  </label>
                </div>

                {promptSource === 'manual' ? (
                  <>
                    {prompts.map((prompt, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                        <TextInput
                          value={prompt}
                          onChange={(val) => updatePromptInput(idx, val)}
                          placeholder={`Prompt ${idx + 1}`}
                        />
                        {prompts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePromptInput(idx)}
                            style={{ ...buttonIcon }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addPromptInput} style={{ ...buttonSmall, marginBottom: '12px' }}>
                      + Add prompt
                    </button>
                    <div style={{ marginBottom: '24px' }}>
                      <label htmlFor="prompts-csv" style={{ fontSize: '13px', fontWeight: 500, color: colors.teal, textDecoration: 'underline', cursor: 'pointer' }}>
                        Or upload CSV
                      </label>
                      <input
                        id="prompts-csv"
                        type="file"
                        accept=".csv"
                        onChange={handlePromptsFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </>
                ) : (
                  <div style={{ marginBottom: '24px' }}>
                    {entitySets.length === 0 ? (
                      <div style={{ fontSize: '13px', color: colors.textMuted, fontStyle: 'italic' }}>
                        No entity sets found. Create one first, or add prompts manually.
                      </div>
                    ) : (
                      <>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '8px' }}>
                          Select entity set
                        </label>
                        <select
                          value={entitySetId}
                          onChange={(e) => setEntitySetId(e.target.value)}
                          style={{
                            width: '100%', padding: '10px 12px', fontSize: '14px', fontFamily: FONT,
                            color: colors.primary, backgroundColor: 'white',
                            border: `1px solid ${colors.border}`, borderRadius: '6px',
                          }}
                        >
                          <option value="">Select an entity set…</option>
                          {entitySets.map(es => (
                            <option key={es.id} value={es.id}>{es.name || es.id}</option>
                          ))}
                        </select>
                        <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '8px' }}>
                          Prompts and URLs from this set will be imported on creation.
                        </div>
                        {entitySetId && (
                          <div style={{
                            marginTop: '12px', padding: '12px',
                            backgroundColor: colors.bgSubtle, border: `1px solid ${colors.border}`,
                            borderRadius: '6px', fontSize: '13px', color: colors.primary,
                          }}>
                            <strong>Importing from:</strong> {getEntitySetName(entitySetId)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: `1px solid ${colors.border}` }}>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '12px' }}>
                      Engines
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      {ENGINES.map(e => (
                        <label key={e.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={engines.includes(e.key)}
                            onChange={() => toggleEngine(e.key)}
                            style={{ width: '18px', height: '18px', accentColor: colors.teal }}
                          />
                          <span style={{ fontSize: '14px', color: colors.primary }}>{e.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: colors.primary, marginBottom: '12px' }}>
                      Audit Frequency
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {FREQUENCIES.map(f => (
                        <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="frequency"
                            value={f.key}
                            checked={frequency === f.key}
                            onChange={() => setFrequency(f.key)}
                            style={{ width: '16px', height: '16px', accentColor: colors.teal, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', color: colors.primary }}>{f.label}</span>
                          <span style={{ fontSize: '12px', color: colors.textMuted }}>({f.hint})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            {error && <ErrorCard message={error} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={handleBack} style={buttonSecondary}>
                &lt;back to campaign modules
              </button>
              <button type="button" onClick={handleNext} style={buttonPrimary}>
                Next
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div style={{ padding: '28px 32px', fontFamily: FONT, maxWidth: '600px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: colors.primary, margin: '0 0 24px' }}>
              URLs
            </h2>
            <Card>
              <div style={{ padding: '20px' }}>
                {urls.map((url, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                    <TextInput
                      value={url}
                      onChange={(val) => updateUrlInput(idx, val)}
                      placeholder={`URL ${idx + 1}`}
                    />
                    {urls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeUrlInput(idx)}
                        style={{ ...buttonIcon }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addUrlInput} style={{ ...buttonSmall, marginBottom: '12px' }}>
                  + Add URL
                </button>
                <div style={{ marginTop: '12px' }}>
                  <label htmlFor="urls-csv" style={{ fontSize: '13px', fontWeight: 500, color: colors.teal, textDecoration: 'underline', cursor: 'pointer' }}>
                    Or upload CSV
                  </label>
                  <input
                    id="urls-csv"
                    type="file"
                    accept=".csv"
                    onChange={handleUrlsFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </Card>
            {error && <ErrorCard message={error} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={handleBack} style={buttonSecondary}>
                &lt;back to prompts
              </button>
              <button type="button" onClick={handleNext} style={buttonPrimary}>
                Next
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div style={{ padding: '28px 32px', fontFamily: FONT, maxWidth: '600px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: colors.primary, margin: '0 0 24px' }}>
              Competitors
            </h2>
            <Card>
              <div style={{ padding: '20px' }}>
                {competitors.map((competitor, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                    <TextInput
                      value={competitor}
                      onChange={(val) => updateCompetitorInput(idx, val)}
                      placeholder={`Competitor ${idx + 1}`}
                    />
                    {competitors.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCompetitorInput(idx)}
                        style={{ ...buttonIcon }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addCompetitorInput} style={{ ...buttonSmall, marginBottom: '12px' }}>
                  + Add competitor
                </button>
                <div style={{ marginTop: '12px' }}>
                  <a href="#" onClick={(e) => e.preventDefault()} style={{ fontSize: '13px', fontWeight: 500, color: colors.textMuted, textDecoration: 'none' }}>
                    Or use AI suggestions
                  </a>
                </div>
              </div>
            </Card>
            {error && <ErrorCard message={error} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={handleBack} style={buttonSecondary}>
                &lt;back to URLs
              </button>
              <button type="button" onClick={handleNext} style={buttonPrimary}>
                Review Campaign
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div style={{ padding: '28px 32px', fontFamily: FONT, maxWidth: '600px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: colors.primary, margin: '0 0 24px' }}>
              Review Campaign
            </h2>
            <Card>
              <div>
                <ReviewRow label="Campaign Name" value={campaignName} />
                <ReviewRow label="Brand" value={brand} />
                <ReviewRow label="Domain" value={domain} />
                <ReviewRow label="Target Market" value={targetMarket} />
                <ReviewRow label="Modules" value={allModulesSelected ? 'All Modules' : modules.map(moduleLabel).join(', ')} />
                <ReviewRow label="Prompts" value={
                  promptSource === 'enq' && entitySetId
                    ? `Entity set import + ${cleanPrompts.length} manual`
                    : `${cleanPrompts.length} prompt(s)`
                } />
                {entitySetId && <ReviewRow label="Entity Set" value={getEntitySetName(entitySetId)} />}
                <ReviewRow label="URLs" value={`${cleanUrls.length} URL(s)`} />
                <ReviewRow label="Competitors" value={cleanCompetitors.length ? `${cleanCompetitors.length} competitor(s)` : 'None'} />
                <ReviewRow label="Engines" value={engines.join(', ')} />
                <ReviewRow label="Frequency" value={frequencyLabel()} last />
              </div>
            </Card>
            {error && <ErrorCard message={error} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={handleBack} style={buttonSecondary} disabled={submitting}>
                &lt;back to competitors
              </button>
              <button type="button" onClick={handleCreate} style={buttonPrimary} disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Campaign'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <SidebarLayout campaigns={allCampaigns} apiBaseUrl={apiBase}>
      {renderContent()}
    </SidebarLayout>
  );
}

export default CampaignBuilder;
